import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Layout } from '../components/Layout';
import { useEffect, useState } from 'react';
import { Site, listSites, invalidateCache, createSnapshot } from '../api/sites';

export function HomePage() {
  const { user, isAuthenticated, currentTenant } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadSites();
    }
  }, [isAuthenticated]);

  // Auto-dismiss action messages
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => setActionMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  async function loadSites() {
    try {
      setLoading(true);
      const data = await listSites();
      setSites(data || []);
    } catch (err) {
      console.error('Failed to load sites:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleInvalidateCache(domain: string) {
    setActionLoading(`invalidate-${domain}`);
    try {
      await invalidateCache(domain);
      setActionMessage({ type: 'success', text: `Cache invalidation started for ${domain}` });
    } catch (err) {
      setActionMessage({ type: 'error', text: `Failed to invalidate cache: ${err instanceof Error ? err.message : 'Unknown error'}` });
    } finally {
      setActionLoading(null);
    }
  }

  async function handleCreateSnapshot(domain: string) {
    setActionLoading(`snapshot-${domain}`);
    try {
      await createSnapshot(domain);
      setActionMessage({ type: 'success', text: `Snapshot created for ${domain}` });
    } catch (err) {
      setActionMessage({ type: 'error', text: `Failed to create snapshot: ${err instanceof Error ? err.message : 'Unknown error'}` });
    } finally {
      setActionLoading(null);
    }
  }

  const currentTenantInfo = user?.tenants?.find(t => t.id === currentTenant);

  return (
    <Layout appName="Site Manager">
      <div className="py-6 sm:py-12">
        {/* Authenticated user - show sites dashboard */}
        {isAuthenticated && user ? (
          <div className="max-w-6xl mx-auto px-4 sm:px-0">
            {/* Dashboard Header */}
            <div className="mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Your Sites
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {currentTenantInfo ? (
                  <>Managing sites for <span className="font-medium">{currentTenantInfo.name}</span></>
                ) : (
                  'Manage your static websites'
                )}
              </p>
            </div>

            {/* Action message toast */}
            {actionMessage && (
              <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                actionMessage.type === 'success' 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800' 
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
              }`}>
                <span className="text-lg">{actionMessage.type === 'success' ? '✓' : '✕'}</span>
                <span className="flex-1">{actionMessage.text}</span>
                <button onClick={() => setActionMessage(null)} className="text-lg opacity-60 hover:opacity-100">×</button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : sites.length === 0 ? (
              <div className="text-center py-12 sm:py-16 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No sites yet</h3>
                <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding your first website.</p>
                <Link
                  to="/sites"
                  className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Add Your First Site
                </Link>
              </div>
            ) : (
              <>
                {/* Sites grid with quick actions */}
                <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {sites.slice(0, 6).map((site) => (
                    <div
                      key={site.domain}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow overflow-hidden"
                    >
                      {/* Site header */}
                      <Link
                        to={`/sites/${site.domain}`}
                        className="block p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white truncate">
                            {site.domain}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ml-2 ${
                            site.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            site.status === 'pending_dns' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {site.status.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {site.cf_distribution_id ? '✓ CDN active' : 'DNS pending'}
                        </p>
                      </Link>
                      
                      {/* Quick actions */}
                      {site.status === 'active' && (
                        <div className="border-t border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-800/50">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleInvalidateCache(site.domain)}
                              disabled={actionLoading === `invalidate-${site.domain}`}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                              title="Invalidate CloudFront cache"
                            >
                              {actionLoading === `invalidate-${site.domain}` ? (
                                <span className="animate-spin mr-1.5">⟳</span>
                              ) : (
                                <span className="mr-1.5">🔄</span>
                              )}
                              Clear Cache
                            </button>
                            <button
                              onClick={() => handleCreateSnapshot(site.domain)}
                              disabled={actionLoading === `snapshot-${site.domain}`}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                              title="Create a backup snapshot"
                            >
                              {actionLoading === `snapshot-${site.domain}` ? (
                                <span className="animate-spin mr-1.5">⟳</span>
                              ) : (
                                <span className="mr-1.5">📸</span>
                              )}
                              Snapshot
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {sites.length > 6 && (
                  <div className="mt-6 text-center">
                    <Link to="/sites" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                      View all {sites.length} sites →
                    </Link>
                  </div>
                )}
                <div className="mt-6 sm:mt-8">
                  <Link
                    to="/sites"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Manage Sites
                  </Link>
                </div>
              </>
            )}
          </div>
        ) : (
          /* Guest user - polished marketing landing page */
          <div className="px-4 sm:px-0">
            {/* Hero Section */}
            <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16">
              {/* Logo/Brand */}
              <div className="mb-6 sm:mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg mb-4">
                  <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                </div>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
                Simple Static Site Hosting
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
                Host, manage, and update your static websites with ease.
                No servers to configure, no deployments to worry about—just upload and go live.
              </p>

              {/* CTA Card */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 sm:p-8 max-w-md mx-auto">
                <p className="text-gray-700 dark:text-gray-300 mb-4 font-medium">
                  Ready to get started?
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Click <strong>Sign In</strong> in the header to manage your websites
                </p>
              </div>
            </div>

            {/* Feature cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto mb-12 sm:mb-16">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-2xl">📁</span>
                </div>
                <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">File Browser</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  Browse and edit your site files directly in the browser. No FTP needed.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-2xl">📸</span>
                </div>
                <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">Auto Snapshots</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  Automatic backups before changes. Restore any previous version instantly.
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow sm:col-span-2 lg:col-span-1">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-4">
                  <span className="text-2xl">🤖</span>
                </div>
                <h3 className="font-semibold text-lg mb-2 text-gray-900 dark:text-white">Virtual Webmaster</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  Submit change requests and let our AI handle the updates for you.
                </p>
              </div>
            </div>

            {/* How It Works */}
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">
                How It Works
              </h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg sm:text-xl">1</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-base sm:text-lg">Add your domain</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base mt-1">Point your domain's nameservers to us and we handle the rest.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg sm:text-xl">2</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-base sm:text-lg">Upload your files</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base mt-1">Use our file browser or sync from your local machine.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <span className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-600 flex items-center justify-center text-white font-bold text-lg sm:text-xl">✓</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-base sm:text-lg">You're live!</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base mt-1">Your site is served globally via CloudFront CDN with HTTPS.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
